import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fmtShort } from '../utils/melDate';

/**
 * MessagesPanel — one table of message threads (one row per Job Assignment)
 * plus a thread modal. Used by both the casual (Messages tab) and the
 * supervisor (Messages tab).
 *
 * props
 *   threads    [{ assignmentId, casual, jobLocation, jobDate, lastFrom, lastTo,
 *                 lastAt, status: 'New'|'Seen', unread, messages: [{ id, from, to, text, createdAt }] }]
 *   loading    bool
 *   me         my display name (to colour my own bubbles)
 *   onSend     (assignmentId, text) => Promise
 *   onOpen     (assignmentId) => Promise   — marks the thread seen
 *   onRefresh  () => void
 *   accent     colour
 *   showCasual show a Casual column (supervisor side)
 */

/* Lock body scroll for the life of a modal. Mount-only, so re-renders never
   re-capture 'hidden' as the value to restore (that bug left the profile page
   unscrollable after leaving this app). */
export const useLockBody = () => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
};

export const BellIcon = ({ live, count, accent }) => (
  <span className={`ca-bell ca-bell-inline ${live ? 'live' : ''}`} aria-label={live ? `${count} unread` : 'No unread'}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
    {live && <span className="ca-bell-count" style={{ background: accent }}>{count}</span>}
  </span>
);

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })} ${d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}`;
};

/* ── thread modal ───────────────────────────────────────────── */
export const ThreadModal = ({ thread, me, onClose, onSend, accent, side = 'casual' }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const endRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useLockBody();

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [thread.messages.length]);

  const send = async () => {
    if (busy || !text.trim()) return;
    setBusy(true); setErr('');
    try { await onSend(thread.assignmentId, text.trim()); setText(''); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // "mine" by who sent it, not by name (logins and record names differ)
  const mySide = side === 'casual' ? 'Casual' : 'Supervisor';
  const mine = (m) => (m.sender ? m.sender === mySide : (m.from || '').toLowerCase() === (me || '').toLowerCase());

  // Portal to <body> with the top-most overlay class: the thread can be opened
  // from a record that is itself open over the Assign form (Open Slots), so it
  // must not depend on DOM order or the tab content's stacking context.
  return createPortal(
    <div className="ca-overlay ca-overlay-top" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ca-rec ca-thread" role="dialog" aria-modal="true" aria-label="Messages">
        <div className="ca-rec-head">
          <div className="ca-rec-title">{thread.jobLocation || 'Job'}<span className="ca-rec-dot"> · </span><span className="ca-rec-dates">{thread.jobDate ? fmtShort(thread.jobDate) : ''}</span></div>
          <div className="ca-rec-sub"><span className="ca-rec-home">{thread.casual}</span><span className="ca-rec-km">{thread.messages.length} message{thread.messages.length === 1 ? '' : 's'}</span></div>
        </div>

        <div className="ca-rec-body ca-thread-body">
          {thread.messages.length === 0 && (
            <div className="ca-no-jobs">No messages yet — start the conversation below.</div>
          )}
          {thread.messages.map((m) => (
            <div key={m.id} className={`ca-msg ${mine(m) ? 'ca-msg-mine' : ''}`}>
              <div className="ca-msg-meta">
                <span className="ca-msg-from">{mine(m) ? 'You' : m.from}</span>
                <span className="ca-msg-to">to {m.to}</span>
                <span className="ca-msg-when">{when(m.createdAt)}</span>
                {m.status === 'New' && !mine(m) && <span className="ca-msg-new">new</span>}
              </div>
              <div className="ca-msg-text" style={mine(m) ? { borderColor: `${accent}66` } : undefined}>{m.text}</div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="ca-rec-foot ca-thread-foot">
          <textarea
            className="ca-thread-input"
            rows={2}
            placeholder="Type a message…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
          />
          {err && <div className="ca-error">{err}</div>}
          <div className="ca-thread-actions">
            <button type="button" className="ca-btn ghost" onClick={onClose}>Close</button>
            <button type="button" className={`ca-btn solid ${busy ? 'is-busy' : ''}`} style={{ background: accent }} disabled={busy || !text.trim()} aria-busy={busy} onClick={send}>
              {busy && <span className="sv-spin" aria-hidden="true" />}{busy ? 'Sending…' : 'Send message'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/* ── panel ──────────────────────────────────────────────────── */
const MessagesPanel = ({ threads, loading, me, onSend, onOpen, onRefresh, accent = '#F97316', showCasual = false, side = 'casual' }) => {
  const [openId, setOpenId] = useState(null);
  const open = threads.find((t) => t.assignmentId === openId) || null;
  const unreadTotal = threads.reduce((n, t) => n + (t.unread || 0), 0);

  const view = async (t) => {
    setOpenId(t.assignmentId);
    if (t.unread) { try { await onOpen(t.assignmentId); } catch { /* keep the modal open regardless */ } }
  };

  return (
    <div className="ca-messages">
      <div className="ca-messages-head">
        <div className="ca-messages-title">
          Messages
          <BellIcon live={unreadTotal > 0} count={unreadTotal} accent={accent} />
        </div>
        <button type="button" className="ca-btn ghost" onClick={onRefresh}>Refresh</button>
      </div>

      {loading && threads.length === 0 && <div className="ca-skeleton" />}
      {!loading && threads.length === 0 && (
        <div className="ca-empty"><div className="ca-empty-title">No messages yet</div><p>Messages about a job appear here, one row per job. Open a row to read the thread and reply.</p></div>
      )}

      {threads.length > 0 && (
        <div className="ca-table-wrap">
          <table className="ca-table ca-msg-table">
            <thead>
              <tr>
                <th>Job Location</th>
                <th>Dates</th>
                {showCasual && <th>Casual</th>}
                <th>Message From</th>
                <th>Message To</th>
                <th>Status</th>
                <th className="ca-th-bell">Message</th>
              </tr>
            </thead>
            <tbody>
              {threads.map((t) => (
                <React.Fragment key={t.assignmentId}>
                  <tr className={`ca-tr ${t.unread ? 'ca-tr-unread' : ''}`} onClick={() => view(t)}>
                    <td data-label="Job Location" className="ca-td-loc"><div className="ca-td-label">{t.jobLocation || '—'}</div></td>
                    <td data-label="Dates" className="ca-td-dates">{t.jobDate ? fmtShort(t.jobDate) : '—'}</td>
                    {showCasual && <td data-label="Casual" className="ca-td-casual">{t.casual || '—'}</td>}
                    <td data-label="Message From" className="ca-td-from">{t.lastFrom}</td>
                    <td data-label="Message To" className="ca-td-to">{t.lastTo}</td>
                    <td data-label="Status" className="ca-td-status">
                      <button type="button" className={`ca-msg-status ${t.status === 'New' ? 'new' : 'seen'}`} style={t.status === 'New' ? { borderColor: accent, color: accent } : undefined} onClick={(e) => { e.stopPropagation(); view(t); }}>
                        {t.status}
                      </button>
                    </td>
                    <td data-label="Message" className="ca-td-bell"><BellIcon live={t.unread > 0} count={t.unread} accent={accent} /></td>
                  </tr>
                  <tr className="ca-tr-foot" onClick={() => view(t)}>
                    <td colSpan={showCasual ? 7 : 6}>
                      <span className="ca-view-link">Click here to view messages<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg></span>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <ThreadModal thread={open} me={me} side={side} onClose={() => setOpenId(null)} onSend={onSend} accent={accent} />}
    </div>
  );
};

export default MessagesPanel;